import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { getSamplePointRecord, sendSamplePointRecord } from '../../../../helpers/api';
import { useEffect, useState } from 'react';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import { parseString4 } from '../../../../helpers/utils';
import { openNotification } from '../../../../helpers/notification';
import { reportStatuses } from '../../../../helpers/appstate';

export default function StandardResultMeasurementInputs({ report, samplePoint, onRefresh }) {
  
  const [samplePointParameter, setSamplePointParameter] = useState([]);
  const [samplePointParemeterLoading, setSamplePointParameterLoading] = useState(false);
  const [inputList, setInputList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [isReadView, setIsReadView] = useState(false);

  //console.log('report', report);
  //console.log('samplePointParameter', samplePointParameter);
  // console.log('parameterList', parameterList);
  // console.log('resultList', resultList);
  //console.log('inputList', inputList);

  useEffect(() => {
    fetchSamplePointParameters();
  }, [samplePoint]);

  useEffect(() => {
    if (refresh) {
      fetchSamplePointParameters();
    }
  }, [refresh]);

  const fetchSamplePointParameters = async () => {
    setSamplePointParameterLoading(true);
    try {
      const res = await getSamplePointRecord(samplePoint?.samplePointId, report?.reportId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setSamplePointParameter(data);

        const flattenResultList = data?.results?.flatMap((i) => i.data) || [];

        let initInputList = [];

        data?.parameters?.map((i) => {
          const resultValue =
            flattenResultList?.find((x) => x?.parameterId === i?.parameterId)?.resultValue || '';

          const modifiedInputList = {
            parameterId: i?.parameterId,
            unit: i?.unit,
            resultValue: resultValue,
          };

          initInputList.push(modifiedInputList);
        });
        setInputList(initInputList);

        const computedCount = initInputList?.filter((x) => x?.resultValue !== '')?.length;
        const parameterCount = data?.parameters?.length;

        // console.log('computedCount', computedCount);
        // console.log('computedList', initInputList);
        // console.log('parameterCount', parameterCount);
        // console.log('parameterList', data?.parameters);

        if (computedCount === parameterCount) {
          setIsReadView(true);
        } else {
          setIsReadView(false);
        }
      }
    } catch (error) {
    } finally {
      setSamplePointParameterLoading(false);
      setRefresh(false);
    }
  };

  const handleInputChange = (newValue, itemToUpdate) => {
    const updatedList = inputList.map((item) => {
      if (item.parameterId === itemToUpdate.parameterId) {
        return { ...item, resultValue: newValue };
      }
      return item;
    });
    setInputList(updatedList);
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const measurements = inputList
        ?.filter((x) => x?.resultValue !== '')
        ?.map((i) => {
          return {
            parameterId: i?.parameterId,
            value: Number(i?.resultValue),
            entryDate: new Date().toISOString(),
          };
        });

      if (measurements?.length !== inputList?.length) {
        openNotification({
          type: 'error',
          message: 'All neccesary data have not been captured',
        });
        return;
      }

      const payload = {
        reportId: report?.reportId,
        facilityId: report?.facilityId,
        samplePointId: samplePoint.samplePointId,
        measurmentId: samplePoint?.measurementId,
        measurements: [measurements],
      };

      // console.log('payload', payload);
      const res = await sendSamplePointRecord(payload);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        openNotification({ type: 'success', message: status?.desc });

        setTimeout(() => {
          setRefresh(true);
          onRefresh(samplePoint);
        }, 1000);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errMsg = error?.data?.result?.status?.desc;
      openNotification({
        type: 'error',
        message: errMsg ?? 'something went wrong! Please try again.',
      });
    } finally {
      setSaving(false);
      //setRefresh(true);
    }
  };

  const handleContentRendering = () => {
    if (samplePointParemeterLoading) {
      return <LoaderSkeleton />;
    } else {
      if (!samplePointParemeterLoading && !samplePointParameter) {
        return <Empty />;
      } else {
        return renderContent();
      }
    }
  };

  const renderContent = () => {
    return (
      <Box>
        <Box>
          <Typography sx={{ fontWeight: 600 }}>{samplePointParameter?.samplePointName}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {samplePointParameter?.groupName}
          </Typography>
        </Box>
        <Box sx={{ mt: 2 }}>{isReadView ? renderReadView() : renderInputView()}</Box>
      </Box>
    );
  };

  const renderInputView = () => {
    return (
      <Box>
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100px, 100%), 1fr))',
            gap: 2,
          }}
        >
          {inputList?.map((i) => {
            return (
              <TextField
                key={i?.parameterId}
                label={parseString4(i?.unit)}
                value={i?.resultValue || ''}
                fullWidth
                onChange={(e) => handleInputChange(e.target.value, i)}
                sx={{ backgroundColor: '#FFFFFF' }}
              />
            );
          })}
        </Box>

        {(report?.reportStatus === reportStatuses.DRAFT || report?.reportStatus === reportStatuses.REJECTED) && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving && <CircularProgress size={20} />}
              sx={{ textTransform: 'none' }}
            >
              Save
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const renderReadView = () => {
    return (
      <Box>
        <Box>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ background: '#699ad0' }}>
                <TableRow>
                  {inputList?.map((headerItem) => {
                    return (
                      <TableCell sx={{ border: '1px solid lightgray', color: 'white' }}>
                        <Typography
                          variant="body2"
                          dangerouslySetInnerHTML={{ __html: headerItem?.unit }}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody TableHead sx={{ background: '#FFFFFF' }}>
                {inputList?.map((bodyItemData) => {
                  return (
                    <TableCell sx={{ border: '1px solid lightgray', textAlign: 'right' }}>
                      <Typography
                        variant="body2"
                        sx={{ padding: '1rem 0' }}
                        dangerouslySetInnerHTML={{ __html: bodyItemData?.resultValue?.toFixed(2) }}
                      />
                    </TableCell>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {(report?.reportStatus === reportStatuses.DRAFT || report?.reportStatus === reportStatuses.REJECTED) && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                setIsReadView(false);
              }}
              disabled={saving}
              startIcon={saving && <CircularProgress size={20} />}
              sx={{ textTransform: 'none' }}
            >
              Edit
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>{handleContentRendering()}</Paper>;
}
